import * as React from 'react';
import { useIntl } from 'react-intl';

import { useApolloClient } from '@apollo/client';

import {
  CreateListMutation,
  CreateListMutationVariables,
  RenameListMutation,
  RenameListMutationVariables,
  WishlistDeleteListMutation,
  WishlistDeleteListMutationVariables,
  WishlistList,
  WishlistListsQuery,
  WishlistShareListMutation,
  WishlistShareListMutationVariables,
} from '@notino/shared/definitions/types';

import messages from './messages';
import createListMutation from './queries/createList.graphql';
import deleteListtMutation from './queries/deleteList.graphql';
import renameListMutation from './queries/renameList.graphql';
import shareListQuery from './queries/shareList.graphql';
import wishlistListsQuery from './queries/wishlistLists.graphql';

const DEFAULT_LIST_ID = 'default';

export const useWishlistLists = () => {
  const client = useApolloClient();
  const { formatMessage } = useIntl();
  const lastFetchedListsRef = React.useRef<WishlistList[]>();

  const fetchLists = async () => {
    try {
      const { data } = await client.query<WishlistListsQuery>({
        query: wishlistListsQuery,
        fetchPolicy: 'no-cache',
      });
      const lists = data?.WishlistLists?.lists ?? [];

      const result = lists.map((list) => ({
        ...list,
        name:
          list.id === DEFAULT_LIST_ID
            ? formatMessage(messages.defaultListName)
            : list.name,
      }));

      lastFetchedListsRef.current = result;
      return result;
    } catch (e) {
      lastFetchedListsRef.current = [];
      return [];
    }
  };

  const createList = async ({
    name,
    productIds,
  }: {
    name: string;
    productIds: string[];
  }) => {
    const { data } = await client.mutate<
      CreateListMutation,
      CreateListMutationVariables
    >({
      mutation: createListMutation,
      variables: {
        name,
        productIds,
      },
    });
    return data?.WishlistCreateList ?? null;
  };

  const renameList = async ({
    name,
    listId,
  }: {
    name: string;
    listId: string;
  }) => {
    const { data } = await client.mutate<
      RenameListMutation,
      RenameListMutationVariables
    >({
      mutation: renameListMutation,
      variables: {
        name,
        listId,
      },
    });
    return data?.WishlistUpdateList ?? null;
  };

  const deleteList = async ({ id }: { id: string }) => {
    const { data } = await client.mutate<
      WishlistDeleteListMutation,
      WishlistDeleteListMutationVariables
    >({
      mutation: deleteListtMutation,
      variables: {
        id,
      },
    });
    return data?.WishlistDeleteList ?? null;
  };

  const shareList = async ({
    listId,
    sharedListId,
  }: {
    listId: string;
    sharedListId: string;
  }) => {
    const { data } = await client.mutate<
      WishlistShareListMutation,
      WishlistShareListMutationVariables
    >({
      mutation: shareListQuery,
      variables: {
        listId,
        sharedListId,
      },
    });
    return data?.WishlistShareList ?? null;
  };

  return {
    fetchLists,
    renameList,
    createList,
    deleteList,
    shareList,
    lastFetchedListsRef,
  };
};

useWishlistLists.DEFAULT_LIST_ID = DEFAULT_LIST_ID;
